import React from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { del, get, post, patch } from '@/lib/api'
import type { ConnectorDTO, ConnectorListResponse } from '@/lib/types'
import { toast } from 'sonner'
import { Button } from '@/components/common/button'
import { StatusBadge } from '@/components/common/status-badge'
import { runBatch } from '@/lib/batch'

function Modal({ open, onClose, children }: { open: boolean; onClose: () => void; children: React.ReactNode }) {
  if (!open) return null
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative z-10 w-[min(640px,96vw)] rounded-lg border bg-background p-4 shadow-lg">
        {children}
      </div>
    </div>
  )
}

function Spinner({ className }: { className?: string }) {
  return <span className={`inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent align-[-0.125em] ${className || ''}`} />
}

function Icon({ name, className }: { name: string; className?: string }) {
  const common = 'w-4 h-4'
  const cls = `${common} ${className || ''}`
  switch (name) {
    case 'archive':
      return <svg className={cls} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="4" rx="1"/><path d="M5 7v14h14V7M10 11h4"/></svg>
    case 'plus':
      return (
        <svg className={cls} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 5v14M5 12h14"/></svg>
      )
    case 'refresh':
      return (
        <svg className={cls} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12a9 9 0 1 1-3-6.7"/><path d="M21 3v6h-6"/></svg>
      )
    case 'restart':
      return (
        <svg className={cls} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12a9 9 0 1 1-3-6.7"/><path d="M21 3v6h-6"/></svg>
      )
    case 'backfill':
      return (
        <svg className={cls} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 5v10"/><path d="M8 11l4 4 4-4"/></svg>
      )
    case 'pause':
      return (
        <svg className={cls} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10 4h2v16h-2zM14 4h2v16h-2z"/></svg>
      )
    case 'resume':
      return (
        <svg className={cls} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M8 5v14l11-7z"/></svg>
      )
    case 'filter':
      return (
        <svg className={cls} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 3H2l8 9v7l4 2v-9z"/></svg>
      )
    case 'audit':
      return (
        <svg className={cls} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/></svg>
      )
    case 'logs':
      return (
        <svg className={cls} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 5h14"/><path d="M3 12h14"/><path d="M3 19h14"/><path d="M17 5h4v14h-4"/></svg>
      )
    case 'key':
      return (
        <svg className={cls} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="7.5" cy="15.5" r="5.5"/><path d="M21 2l-8.5 8.5"/><path d="M17 2h4v4"/></svg>
      )
    case 'link':
      return (
        <svg className={cls} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10 13a5 5 0 0 1 7-7l1 1"/><path d="M14 11a5 5 0 0 1-7 7l-1-1"/><path d="M8 12h8"/></svg>
      )
    case 'trash':
      return (
        <svg className={cls} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4h6v2"/></svg>
      )
    case 'google':
      // Colorful Google "G" logo
      return (
        <svg className={cls} viewBox="0 0 24 24" aria-hidden="true">
          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
          <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
        </svg>
      )
    case 'microsoft':
      // Colorful Microsoft logo (4 colored squares)
      return (
        <svg className={cls} viewBox="0 0 24 24" aria-hidden="true">
          <rect x="3" y="3" width="8.5" height="8.5" fill="#F25022"/>
          <rect x="12.5" y="3" width="8.5" height="8.5" fill="#7FBA00"/>
          <rect x="3" y="12.5" width="8.5" height="8.5" fill="#00A4EF"/>
          <rect x="12.5" y="12.5" width="8.5" height="8.5" fill="#FFB900"/>
        </svg>
      )
    case 'sendgrid':
      return (
        <svg className={cls} viewBox="0 0 24 24" aria-hidden="true" fill="none">
          <rect x="4" y="4" width="6" height="6" rx="1" fill="#1A82E2"/>
          <rect x="14" y="4" width="6" height="6" rx="1" fill="#00B3E6"/>
          <rect x="4" y="14" width="6" height="6" rx="1" fill="#00C389"/>
          <rect x="14" y="14" width="6" height="6" rx="1" fill="#F6A800"/>
        </svg>
      )
    default:
      return null
  }
}

function ReauthLink({ id }: { id: string }) {
  const auth = useMutation({
    mutationFn: () => post<{ ok: boolean; url: string; path: string }, {}>(`/connectors/${id}/auth-link`, {}),
  })
  return (
    <Button size="xs"
      disabled={auth.isPending}
      aria-label="Open reauth URL"
      title="Open reauth URL"
      onClick={async () => {
        // Reserve the tab during the click so asynchronous generation is not popup-blocked.
        const popup = window.open('about:blank', '_blank')
        if (!popup) {
          toast.error('Allow pop-ups to open the reauthentication page')
          return
        }
        popup.opener = null
        try {
          const { url } = await auth.mutateAsync()
          const target = new URL(url)
          target.searchParams.set('auto', '1')
          if (!popup.closed) popup.location.replace(target.toString())
        } catch (error: any) {
          popup.close()
          toast.error(error?.message || 'Failed to get reauth link')
        }
      }}
    >
      {auth.isPending ? <Spinner /> : <Icon name="key" />}
    </Button>
  )
}

function parseIsoMs(value: string | null | undefined): number | null {
  if (!value) return null
  const ms = Date.parse(value)
  return Number.isFinite(ms) ? ms : null
}

const budapestDateTimeFormatter = new Intl.DateTimeFormat('hu-HU', {
  timeZone: 'Europe/Budapest',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false
})

function formatBudapestDateTime(value: string | null | undefined): string {
  if (!value) return '-'
  const ms = parseIsoMs(value)
  if (ms === null) return '-'
  return budapestDateTimeFormatter.format(new Date(ms))
}

export default function ConnectorsPage() {
  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey: ['connectors'],
    queryFn: ({ signal }) => get<ConnectorListResponse>('/connectors', { signal }),
    staleTime: 30_000,
    refetchInterval: (query) => query.state.data?.items.some((item) => item.syncPhase === 'backfill') ? 10_000 : 30_000,
  })

  // OAuth clients for dropdown (used when creating Microsoft connectors)
  interface OAuthClientDTO { key: string; provider: string; clientId: string }
  interface OAuthClientsResponse { items: OAuthClientDTO[] }
  const { data: oauthData } = useQuery({
    queryKey: ['oauth-clients'],
    queryFn: ({ signal }) => get<OAuthClientsResponse>('/oauth-clients', { signal }),
    staleTime: 5 * 60_000,
  })
  const oauthClients: OAuthClientDTO[] = (oauthData?.items as OAuthClientDTO[]) ?? []

  const items = data?.items ?? []
  const isImported = (it: ConnectorDTO) => Boolean((it as any)?.isImported) || String((it as any)?.oauthClientKey || '').startsWith('backup:')
  const providerName = (it: ConnectorDTO) => {
    const provider = String(it.provider || '').toUpperCase()
    if (provider === 'MICROSOFT') return 'Microsoft'
    if (provider === 'SENDGRID') return 'SendGrid (Service managed)'
    return 'Google'
  }
  const providerIcon = (it: ConnectorDTO) => {
    const provider = String(it.provider || '').toUpperCase()
    if (provider === 'MICROSOFT') return 'microsoft'
    if (provider === 'SENDGRID') return 'sendgrid'
    return 'google'
  }
  const isSendGrid = (it: ConnectorDTO) => String(it.provider || '').toUpperCase() === 'SENDGRID'
  const isRetired = (it: ConnectorDTO) => it.status === 'retired'
  const pushImportedToBottom = (arr: ConnectorDTO[]) => {
    const live: ConnectorDTO[] = []
    const imported: ConnectorDTO[] = []
    const retired: ConnectorDTO[] = []
    for (const it of arr) {
      if (isRetired(it)) retired.push(it)
      else if (isImported(it)) imported.push(it)
      else live.push(it)
    }
    return [...live, ...imported, ...retired]
  }
  // Default sort: errors first, then backfill, then delta, then the rest. Then by email.
  const defaultSorted = React.useMemo(() => {
    const rank = (it: ConnectorDTO): number => {
      const status = String(it.status || '').toLowerCase()
      const phase = String(it.syncPhase || '').toLowerCase()
      const isError = status === 'error' || status === 'token_expired' || phase === 'error'
      if (isError) return 0
      // 1: every other state first (paused, idle, auth_ok, syncing without specific phase)
      if (phase !== 'backfill' && phase !== 'delta' && phase !== 'error') return 1
      // Then backfill, then delta
      if (phase === 'backfill') return 2
      if (phase === 'delta') return 3
      return 1
    }
    const byEmail = (a: ConnectorDTO, b: ConnectorDTO) => String(a.email || '').localeCompare(String(b.email || ''))
    const sorted = [...items].sort((a, b) => {
      const ra = rank(a)
      const rb = rank(b)
      if (ra !== rb) return ra - rb
      return byEmail(a, b)
    })
    return pushImportedToBottom(sorted)
  }, [items])
  // Optional user-selected sorting
  const [sortBy, setSortBy] = React.useState<'default' | 'provider' | 'status' | 'phase' | 'email' | 'messages' | 'filtered' | 'latestSentAt'>('default')
  const [sortDir, setSortDir] = React.useState<'asc' | 'desc'>('asc')
  const sortedItems = React.useMemo(() => {
    if (sortBy === 'default') return defaultSorted
    const dir = sortDir === 'asc' ? 1 : -1
    const arr = [...items]
    arr.sort((a, b) => {
      const sv = (v: any) => (v === null || v === undefined ? '' : v)
      switch (sortBy) {
        case 'provider': {
          const aa = String(a.provider || '').toLowerCase()
          const bb = String(b.provider || '').toLowerCase()
          return aa.localeCompare(bb) * dir
        }
        case 'status': {
          const aa = String(a.status || '').toLowerCase()
          const bb = String(b.status || '').toLowerCase()
          return aa.localeCompare(bb) * dir
        }
        case 'phase': {
          const aa = String(a.syncPhase || '').toLowerCase()
          const bb = String(b.syncPhase || '').toLowerCase()
          return aa.localeCompare(bb) * dir
        }
        case 'email': {
          const aa = String(a.email || '')
          const bb = String(b.email || '')
          return aa.localeCompare(bb) * dir
        }
        case 'messages': {
          const aa = Number(a.messageCount || 0)
          const bb = Number(b.messageCount || 0)
          return (aa - bb) * dir
        }
        case 'filtered': {
          const aa = Number(a.filteredMessageCount || 0)
          const bb = Number(b.filteredMessageCount || 0)
          return (aa - bb) * dir
        }
        case 'latestSentAt': {
          const aa = parseIsoMs((a as any).latestSyncedEmailSentAt)
          const bb = parseIsoMs((b as any).latestSyncedEmailSentAt)
          if (aa === null && bb === null) return 0
          if (aa === null) return 1
          if (bb === null) return -1
          return (aa - bb) * dir
        }
      }
      const aa = sv((a as any)[sortBy])
      const bb = sv((b as any)[sortBy])
      if (typeof aa === 'number' && typeof bb === 'number') return (aa - bb) * dir
      return String(aa).localeCompare(String(bb)) * dir
    })
    return pushImportedToBottom(arr)
  }, [items, defaultSorted, sortBy, sortDir])
  const [params] = useSearchParams()
  const [search, setSearch] = React.useState(params.get('email') || '')
  const visibleItems = React.useMemo(() => {
    const query = search.trim().toLowerCase()
    return sortedItems.filter(it => `${it.email} ${providerName(it)} ${it.status} ${it.syncPhase || ''} ${isRetired(it) ? 'megszűnt' : ''} ${isImported(it) ? 'import' : ''}`.toLowerCase().includes(query))
  }, [sortedItems, search])
  const [editing, setEditing] = React.useState<string | null>(null)
  const [filterForm, setFilterForm] = React.useState<{ enabled: boolean; includePrimary: boolean; extraDomains: string; extraEmails: string }>({ enabled: true, includePrimary: true, extraDomains: '', extraEmails: '' })
  const [recomputeOpen, setRecomputeOpen] = React.useState<{ id: string; email: string } | null>(null)
  const [recomputeBulkOpen, setRecomputeBulkOpen] = React.useState(false)
  const [recomputeMode, setRecomputeMode] = React.useState<'auto' | 'filtered-only' | 'full'>('auto')
  const [recomputeRecheckDup, setRecomputeRecheckDup] = React.useState(false)
  const [recomputeStarted, setRecomputeStarted] = React.useState(false)
  const [recomputeBulkStarted, setRecomputeBulkStarted] = React.useState(false)

  // Create connector form state
  const [showCreate, setShowCreate] = React.useState(false)
  type ConnectorProviderForm = 'google' | 'microsoft'
  const [createForm, setCreateForm] = React.useState({
    provider: 'google' as ConnectorProviderForm,
    email: '',
    // OAuth options
    oauthMode: 'existing' as 'existing' | 'inline',
    oauthClientKey: '',
    clientId: '',
    clientSecret: '',
    redirectUris: '',
    scopesDefault: '',
    // Domain filter
    filterEnabled: true,
    filterIncludePrimary: true,
    filterExtraDomains: '',
    filterExtraEmails: '',
  })

  const qc = useQueryClient()
  const restart = useMutation({
    mutationFn: async (id: string) => post<{ ok: boolean }, {}>(`/connectors/${id}/restart-sync`, {}),
    onSuccess: () => {
      toast.success('Restarted sync')
      qc.invalidateQueries({ queryKey: ['connectors'] })
    },
    onError: (e: any) => toast.error(e?.message || 'Failed to restart'),
  })
  const backfill = useMutation({
    mutationFn: async (id: string) => post<{ ok: boolean }, {}>(`/connectors/${id}/backfill`, {}),
    onSuccess: () => {
      toast.success('Backfill triggered')
      qc.invalidateQueries({ queryKey: ['connectors'] })
    },
    onError: (e: any) => toast.error(e?.message || 'Failed to backfill'),
  })
  const pause = useMutation({
    mutationFn: async (id: string) => post<{ ok: boolean }, {}>(`/connectors/${id}/pause`, {}),
    onSuccess: () => {
      toast.success('Paused')
      qc.invalidateQueries({ queryKey: ['connectors'] })
    },
    onError: (e: any) => toast.error(e?.message || 'Failed to pause'),
  })
  const retire = useMutation({
    mutationFn: async (id: string) => post<{ ok: boolean }, {}>(`/connectors/${id}/retire`, {}),
    onSuccess: () => {
      toast.success('Megszűntként archiválva; a levelek megmaradtak')
      qc.invalidateQueries({ queryKey: ['connectors'] })
    },
    onError: (e: any) => toast.error(e?.message || 'Nem sikerült leállítani a connectort; próbáld újra'),
  })
  const recompute = useMutation({
    mutationFn: async ({ id, mode, recheckDuplicates }: { id: string; mode?: 'auto' | 'filtered-only' | 'full'; recheckDuplicates?: boolean }) =>
      post<{ ok: boolean }, { mode?: 'auto' | 'filtered-only' | 'full'; recheckDuplicates?: boolean }>(`/connectors/${id}/recompute-filter`, { mode, recheckDuplicates }),
    onSuccess: () => {
      toast.success('Recompute filter triggered')
      qc.invalidateQueries({ queryKey: ['connectors'] })
    },
    onError: (e: any) => toast.error(e?.message || 'Failed to recompute filter'),
  })
  const resume = useMutation({
    mutationFn: async (id: string) => post<{ ok: boolean }, {}>(`/connectors/${id}/resume`, {}),
    onSuccess: () => {
      toast.success('Resumed')
      qc.invalidateQueries({ queryKey: ['connectors'] })
    },
    onError: (e: any) => toast.error(e?.message || 'Failed to resume'),
  })
  const audit = useMutation({
    mutationFn: async (id: string) => post<{ ok: boolean }, {}>(`/connectors/${id}/audit`, {}),
    onSuccess: () => {
      toast.success('Audit triggered')
      qc.invalidateQueries({ queryKey: ['connectors'] })
    },
    onError: (e: any) => toast.error(e?.message || 'Failed to trigger audit'),
  })
  const auditAll = useMutation({
    mutationFn: async () => post<{ ok: boolean; triggered?: number; total?: number } | { error: string }, {}>(`/ops/audit`, {}),
    onSuccess: (res: any) => {
      const n = typeof res?.triggered === 'number' ? res.triggered : undefined
      if (res?.failed) toast.warning(`Audit requested: ${n ?? 0} started, ${res.deferred ?? 0} deferred, ${res.failed} failed`)
      else toast.success(`Audit requested: ${n ?? 0} started, ${res.deferred ?? 0} deferred`)
      qc.invalidateQueries({ queryKey: ['connectors'] })
    },
    onError: (e: any) => toast.error(e?.message || 'Failed to trigger audit for all'),
  })
  const remove = useMutation({
    mutationFn: async (args: { id: string; purge: boolean }) => del<{ ok: boolean }>(`/connectors/${args.id}?purgeMessages=${String(args.purge)}`),
    onSuccess: () => {
      toast.success('Removed connector')
      qc.invalidateQueries({ queryKey: ['connectors'] })
    },
    onError: (e: any) => toast.error(e?.message || 'Failed to remove'),
  })
  const updateFilter = useMutation({
    mutationFn: async (args: { id: string; enabled?: boolean; includePrimary?: boolean; extraDomains?: string; extraEmails?: string; resetDropped?: boolean }) => {
      const body: any = {}
      if (args.enabled !== undefined) body.enabled = args.enabled
      if (args.includePrimary !== undefined) body.includePrimary = args.includePrimary
      if (args.extraDomains !== undefined) body.extraDomains = args.extraDomains
      if (args.extraEmails !== undefined) body.extraEmails = args.extraEmails
      if (args.resetDropped === true) body.resetDroppedTotal = true
      return patch<{ ok: boolean; domainFilter: any }, typeof body>(`/connectors/${args.id}/domain-filter`, body)
    },
    onSuccess: () => {
      toast.success('Domain filter updated')
      qc.invalidateQueries({ queryKey: ['connectors'] })
      setEditing(null)
    },
    onError: (e: any) => toast.error(e?.message || 'Failed to update filter'),
  })
  const authLink = useMutation({
    mutationFn: async (id: string) => post<{ ok: boolean; url: string; path: string }, {}>(`/connectors/${id}/auth-link`, {}),
    onSuccess: (res) => {
      const url = (res as any)?.url
      if (url) {
        navigator.clipboard?.writeText(url).catch(() => {})
        toast.success('Shareable auth link copied')
      }
    },
    onError: (e: any) => toast.error(e?.message || 'Failed to get auth link'),
  })

  // Multi-select & bulk actions
  const [selected, setSelected] = React.useState<Record<string, boolean>>({})
  const selectable = React.useMemo(
    () => visibleItems.filter((x) => !isImported(x) && !isSendGrid(x) && !isRetired(x)),
    [visibleItems]
  )
  const visibleIds = React.useMemo(() => selectable.map((x) => x.id), [selectable])
  const allSelected = visibleIds.length > 0 && visibleIds.every((id) => selected[id])
  const selectedIds = React.useMemo(() => Object.keys(selected).filter((k) => selected[k] && visibleIds.includes(k)), [selected, visibleIds])
  const clearSelection = () => setSelected({})
  const [bulkPending, setBulkPending] = React.useState(false)
  const bulkPendingRef = React.useRef(false)
  const reportBatch = (label: string, result: { succeeded: number; failed: string[] }) => {
    if (result.succeeded) toast.success(`${label} for ${result.succeeded} connector(s)`)
    if (result.failed.length) toast.error(`${result.failed.length} connector(s) failed; they remain selected for retry`)
    setSelected(Object.fromEntries(result.failed.map((id) => [id, true])))
    qc.invalidateQueries({ queryKey: ['connectors'] })
  }
  const toggleAllVisible = (checked: boolean) => {
    const next: Record<string, boolean> = { ...selected }
    for (const id of visibleIds) next[id] = checked
    setSelected(next)
  }
  const bulkAudit = async () => {
    const ids = selectedIds
    if (!ids.length) return toast.info('No connectors selected')
    if (bulkPendingRef.current) return
    bulkPendingRef.current = true
    setBulkPending(true)
    try {
      reportBatch('Audit triggered', await runBatch(ids, (id) => post<{ ok: boolean }, {}>(`/connectors/${id}/audit`, {})))
    } catch (e: any) {
      toast.error(e?.message || 'Failed to trigger audit for selection')
    } finally {
      bulkPendingRef.current = false
      setBulkPending(false)
    }
  }
  const bulkRotate = async () => {
    const ids = selectedIds
    if (!ids.length) return toast.info('No connectors selected')
    if (bulkPendingRef.current) return
    bulkPendingRef.current = true
    setBulkPending(true)
    try {
      reportBatch('Rotate requested', await runBatch(ids, (id) => post<{ ok: boolean }, {}>(`/connectors/${id}/rotate`, {})))
    } catch (e: any) {
      toast.error(e?.message || 'Failed to rotate selection')
    } finally {
      bulkPendingRef.current = false
      setBulkPending(false)
    }
  }
  const openBulkRecompute = () => {
    setRecomputeMode('auto')
    setRecomputeRecheckDup(false)
    setRecomputeBulkStarted(false)
    setRecomputeBulkOpen(true)
  }

  const bulkRecompute = useMutation({
    mutationFn: async (args: { ids: string[]; mode: 'auto' | 'filtered-only' | 'full'; recheckDuplicates: boolean }) => {
      const { ids, mode, recheckDuplicates } = args
      if (!ids.length) throw new Error('No connectors selected')
      return runBatch(ids, (id) => post<{ ok: boolean }, { mode?: 'auto' | 'filtered-only' | 'full'; recheckDuplicates?: boolean }>(`/connectors/${id}/recompute-filter`, { mode, recheckDuplicates }))
    },
    onSuccess: (res) => {
      reportBatch('Recompute started', res)
      setRecomputeBulkStarted(res.failed.length === 0)
    },
    onError: (e: any) => toast.error(e?.message || 'Failed to start recompute for selection'),
  })

  // Create connector
  const createConnector = useMutation({
    mutationFn: async () => {
      const provider = createForm.provider
      const email = createForm.email.trim()
      if (!email) throw new Error('Email is required')
      if (provider === 'microsoft') {
        const hasKey = createForm.oauthMode === 'existing' && createForm.oauthClientKey.trim().length > 0
        const hasInline = createForm.oauthMode === 'inline' && createForm.clientId.trim().length > 0 && createForm.clientSecret.trim().length > 0
        if (!hasKey && !hasInline) throw new Error('Microsoft requires an OAuth client key or Client ID + Secret')
      }

      const body: any = {
        provider: provider,
      }
      if (email) body.email = email
      if (provider === 'microsoft') {
        if (createForm.oauthMode === 'existing' && createForm.oauthClientKey.trim()) {
          body.oauthClientKey = createForm.oauthClientKey.trim()
        }
      }
      const shouldIncludeInline = (
        (provider === 'microsoft' && createForm.oauthMode === 'inline') ||
        (provider === 'google')
      ) && createForm.clientId.trim() && createForm.clientSecret.trim()
      if (shouldIncludeInline) {
        body.oauthClient = {
          clientId: createForm.clientId.trim(),
          clientSecret: createForm.clientSecret.trim(),
          redirectUris: createForm.redirectUris.trim() || undefined,
          scopesDefault: createForm.scopesDefault.trim() || undefined,
        }
      }

      const res = await post<{ ok: boolean; id: string }, typeof body>(`/connectors`, body)
      const id = (res as any)?.id as string

      // Optionally set initial domain filter if different from defaults
      const defaults = { enabled: true, includePrimary: true, extraDomains: '', extraEmails: '' }
      const df = {
        enabled: createForm.filterEnabled,
        includePrimary: createForm.filterIncludePrimary,
        extraDomains: (createForm.filterExtraDomains || '').trim(),
        extraEmails: (createForm.filterExtraEmails || '').trim(),
      }
      const differs =
        df.enabled !== defaults.enabled ||
        df.includePrimary !== defaults.includePrimary ||
        df.extraDomains !== defaults.extraDomains ||
        df.extraEmails !== defaults.extraEmails
      if (id && differs) {
        await patch<{ ok: boolean; domainFilter: any }, any>(`/connectors/${id}/domain-filter`, {
          enabled: df.enabled,
          includePrimary: df.includePrimary,
          extraDomains: df.extraDomains,
          extraEmails: df.extraEmails,
        })
      }

      return { id }
    },
    onSuccess: () => {
      toast.success('Connector created')
      setShowCreate(false)
      setCreateForm({
        provider: 'google',
        email: '',
        oauthMode: 'existing',
        oauthClientKey: '',
        clientId: '',
        clientSecret: '',
        redirectUris: '',
        scopesDefault: '',
        filterEnabled: true,
        filterIncludePrimary: true,
        filterExtraDomains: '',
        filterExtraEmails: '',
      })
      qc.invalidateQueries({ queryKey: ['connectors'] })
      qc.invalidateQueries({ queryKey: ['oauth-clients'] })
    },
    onError: (e: any) => toast.error(e?.message || 'Failed to create connector'),
  })

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h1 className="text-2xl font-semibold tracking-tight">Connectors</h1>
        <div className="flex items-center gap-2">
          <Button variant="primary" aria-label={showCreate ? 'Close create connector' : 'Create connector'} onClick={() => setShowCreate((v) => !v)} title={showCreate ? 'Close create connector form' : 'Create a new connector'}>
            <Icon name={showCreate ? 'trash' : 'plus'} />
          </Button>
          <Button variant="primary" aria-label="Refresh connectors list" onClick={() => refetch()} disabled={isFetching} title="Refresh connectors list">
            {isFetching ? <Spinner /> : <Icon name="refresh" />}
          </Button>
          <Button onClick={() => auditAll.mutate()} disabled={auditAll.isPending} title="Run consistency scan on all connectors">
            Run audit for all
          </Button>
        </div>
      </div>
      {showCreate && (
        <div className="rounded-lg border p-4">
          <h2 className="mb-3 text-lg font-medium">Create connector</h2>
          <div className="grid gap-3 md:grid-cols-2">
            <label className="flex flex-col gap-1 text-sm">
              <span className="text-muted-foreground">Provider</span>
              <select
                className="rounded-md border bg-background px-3 py-2"
                value={createForm.provider}
                onChange={(e) => {
                  const provider = e.target.value as ConnectorProviderForm
                  setCreateForm({
                    ...createForm,
                    provider,
                    oauthMode: provider === 'microsoft' ? createForm.oauthMode : 'existing',
                    oauthClientKey: provider === 'microsoft' ? createForm.oauthClientKey : '',
                    clientId: createForm.clientId,
                    clientSecret: createForm.clientSecret,
                    redirectUris: createForm.redirectUris,
                    scopesDefault: createForm.scopesDefault
                  })
                }}
              >
                <option value="google">Google</option>
                <option value="microsoft">Microsoft</option>
              </select>
            </label>
            <label className="flex flex-col gap-1 text-sm">
              <span className="text-muted-foreground">Email</span>
              <input className="rounded-md border bg-background px-3 py-2" value={createForm.email} onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })} placeholder="user@example.com" />
            </label>
            {createForm.provider === 'microsoft' && (
              <>
                <label className="flex flex-col gap-1 text-sm">
                  <span className="text-muted-foreground">OAuth mode</span>
                  <select
                    className="rounded-md border bg-background px-3 py-2"
                    value={createForm.oauthMode}
                    onChange={(e) => setCreateForm({ ...createForm, oauthMode: e.target.value as 'existing' | 'inline' })}
                  >
                    <option value="existing">Use existing OAuth client</option>
                    <option value="inline">Enter Client ID + Secret</option>
                  </select>
                </label>
                {createForm.oauthMode === 'existing' ? (
                  <label className="flex flex-col gap-1 text-sm">
                    <span className="text-muted-foreground">OAuth client key</span>
                    <input
                      list="oauth-client-keys"
                      className="rounded-md border bg-background px-3 py-2"
                      value={createForm.oauthClientKey}
                      onChange={(e) => setCreateForm({ ...createForm, oauthClientKey: e.target.value })}
                      placeholder="ms-..."
                    />
                    <datalist id="oauth-client-keys">
                      {oauthClients
                        .filter((c) => (c.provider || '').toUpperCase() === 'MICROSOFT')
                        .map((c) => (
                          <option key={c.key} value={c.key}>{c.key}</option>
                        ))}
                    </datalist>
                  </label>
                ) : (
                  <>
                    <label className="flex flex-col gap-1 text-sm">
                      <span className="text-muted-foreground">Client ID</span>
                      <input className="rounded-md border bg-background px-3 py-2" autoComplete="off" name="oauth-client-id" value={createForm.clientId} onChange={(e) => setCreateForm({ ...createForm, clientId: e.target.value })} placeholder="xxxxxxxx-..." />
                    </label>
                    <label className="flex flex-col gap-1 text-sm">
                      <span className="text-muted-foreground">Client Secret</span>
                      <input type="password" className="rounded-md border bg-background px-3 py-2" autoComplete="new-password" name="oauth-client-secret" value={createForm.clientSecret} onChange={(e) => setCreateForm({ ...createForm, clientSecret: e.target.value })} placeholder="••••••••" />
                    </label>
                    <label className="flex flex-col gap-1 text-sm md:col-span-2">
                      <span className="text-muted-foreground">Redirect URIs (optional; comma/space separated)</span>
                      <textarea className="rounded-md border bg-background px-3 py-2" rows={2} value={createForm.redirectUris} onChange={(e) => setCreateForm({ ...createForm, redirectUris: e.target.value })} placeholder="https://example.com/auth/callback/microsoft" />
                    </label>
                    <label className="flex flex-col gap-1 text-sm md:col-span-2">
                      <span className="text-muted-foreground">Default scopes (optional; comma/space separated)</span>
                      <textarea className="rounded-md border bg-background px-3 py-2" rows={2} value={createForm.scopesDefault} onChange={(e) => setCreateForm({ ...createForm, scopesDefault: e.target.value })} placeholder="offline_access openid email profile https://graph.microsoft.com/Mail.Read" />
                    </label>
                  </>
                )}
              </>
            )}

            {createForm.provider === 'google' && (
              <>
                <div className="md:col-span-2 text-xs text-muted-foreground">
                  Optional: Enter Google OAuth Client ID and Secret to upsert a dedicated client. If left blank, env GOOGLE_CLIENT_ID/SECRET is used.
                </div>
                <label className="flex flex-col gap-1 text-sm">
                  <span className="text-muted-foreground">Client ID (optional)</span>
                  <input className="rounded-md border bg-background px-3 py-2" autoComplete="off" name="oauth-client-id" value={createForm.clientId} onChange={(e) => setCreateForm({ ...createForm, clientId: e.target.value })} placeholder="xxxxxxxx.apps.googleusercontent.com" />
                </label>
                <label className="flex flex-col gap-1 text-sm">
                  <span className="text-muted-foreground">Client Secret (optional)</span>
                  <input type="password" className="rounded-md border bg-background px-3 py-2" autoComplete="new-password" name="oauth-client-secret" value={createForm.clientSecret} onChange={(e) => setCreateForm({ ...createForm, clientSecret: e.target.value })} placeholder="••••••••" />
                </label>
                <label className="flex flex-col gap-1 text-sm md:col-span-2">
                  <span className="text-muted-foreground">Redirect URIs (optional; comma/space separated)</span>
                  <textarea className="rounded-md border bg-background px-3 py-2" rows={2} value={createForm.redirectUris} onChange={(e) => setCreateForm({ ...createForm, redirectUris: e.target.value })} placeholder="https://example.com/auth/callback/google" />
                </label>
                <label className="flex flex-col gap-1 text-sm md:col-span-2">
                  <span className="text-muted-foreground">Default scopes (optional; comma/space separated)</span>
                  <textarea className="rounded-md border bg-background px-3 py-2" rows={2} value={createForm.scopesDefault} onChange={(e) => setCreateForm({ ...createForm, scopesDefault: e.target.value })} placeholder="https://www.googleapis.com/auth/gmail.readonly openid email profile" />
                </label>
              </>
            )}

            <div className="md:col-span-2 mt-2 rounded-md border p-3">
              <div className="mb-2 text-sm font-medium">Domain filter</div>
              <div className="flex flex-wrap gap-3 mb-3 text-sm">
                <label className="inline-flex items-center gap-2"><input type="checkbox" checked={createForm.filterEnabled} onChange={(e) => setCreateForm({ ...createForm, filterEnabled: e.target.checked })} /> Enabled</label>
                <label className="inline-flex items-center gap-2"><input type="checkbox" checked={createForm.filterIncludePrimary} onChange={(e) => setCreateForm({ ...createForm, filterIncludePrimary: e.target.checked })} /> Include primary domain</label>
              </div>
              <div className="grid gap-3 md:grid-cols-2">
                <label className="flex flex-col gap-1 text-sm">
                  <span className="text-muted-foreground">Extra domains (comma/space separated)</span>
                  <textarea className="rounded-md border bg-background px-3 py-2" rows={2} value={createForm.filterExtraDomains} onChange={(e) => setCreateForm({ ...createForm, filterExtraDomains: e.target.value })} placeholder="example.com other.org" />
                </label>
                <label className="flex flex-col gap-1 text-sm">
                  <span className="text-muted-foreground">Extra emails (comma/space separated)</span>
                  <textarea className="rounded-md border bg-background px-3 py-2" rows={2} value={createForm.filterExtraEmails} onChange={(e) => setCreateForm({ ...createForm, filterExtraEmails: e.target.value })} placeholder="user@example.com other@org.com" />
                </label>
              </div>
            </div>

            <div className="md:col-span-2 flex justify-end gap-2 mt-1">
              <Button onClick={() => setShowCreate(false)}>Cancel</Button>
              <Button variant="primary" onClick={() => createConnector.mutate()} disabled={createConnector.isPending} title="Create connector">
                {createConnector.isPending ? 'Creating…' : 'Create'}
              </Button>
            </div>
          </div>
        </div>
      )}
      {isLoading ? (
        <p className="text-muted-foreground">Loading…</p>
      ) : isError ? (
        <p className="text-destructive">{(error as Error)?.message || 'Failed to load'}</p>
      ) : items.length === 0 ? (
        <p className="text-muted-foreground">No connectors</p>
      ) : (
        <div className="overflow-x-auto">
          <div className="mb-2 flex flex-wrap items-center gap-2">
            <label className="flex flex-col gap-1 text-sm">
              Find connector
              <input type="search" className="rounded-md border bg-background px-3 py-2" value={search} onChange={e => setSearch(e.target.value)} placeholder="Email, provider or status" />
            </label>
            <span className="text-xs text-muted-foreground">{visibleItems.length} / {items.length} connectors</span>
            <label className="inline-flex items-center gap-2 text-sm">
              <span className="text-muted-foreground">Sort by</span>
              <select className="rounded-md border bg-background px-2 py-1" value={sortBy} onChange={(e) => setSortBy(e.target.value as any)}>
                <option value="default">Default</option>
                <option value="provider">Provider</option>
                <option value="status">Status</option>
                <option value="phase">Phase</option>
                <option value="email">Email</option>
                <option value="messages">Messages</option>
                <option value="filtered">Filtered</option>
                <option value="latestSentAt">Latest email sent at</option>
              </select>
              {sortBy !== 'default' && (
                <select className="rounded-md border bg-background px-2 py-1" value={sortDir} onChange={(e) => setSortDir(e.target.value as any)}>
                  <option value="asc">Asc</option>
                  <option value="desc">Desc</option>
                </select>
              )}
            </label>
            <div className="ml-auto flex flex-wrap items-center gap-2">
              <span className="text-xs text-muted-foreground">Selected: {selectedIds.length} ({selectable.length} available)</span>
              <Button onClick={bulkAudit} disabled={selectedIds.length === 0 || bulkPending || bulkRecompute.isPending} title="Run audit on selected">Audit selected</Button>
              <Button onClick={bulkRotate} disabled={selectedIds.length === 0 || bulkPending || bulkRecompute.isPending} title="Rotate Temporal workflows for selected">Rotate selected</Button>
              <Button onClick={openBulkRecompute} disabled={selectedIds.length === 0 || bulkPending || bulkRecompute.isPending} title="Recompute domain filter on selected">Recompute selected</Button>
              {selectedIds.length > 0 && <Button onClick={clearSelection} title="Clear selection">Clear</Button>}
            </div>
          </div>
          <table className="w-full table-auto text-sm">
            <thead>
              <tr className="border-b text-left text-muted-foreground">
                <th className="w-8 py-2 pr-2"><input type="checkbox" checked={allSelected} onChange={(e) => toggleAllVisible(e.target.checked)} aria-label="Select all" /></th>
                <th className="w-16 py-2 pr-2 whitespace-nowrap">Provider</th>
                <th className="py-2 pr-2 whitespace-nowrap">Email</th>
                <th className="w-[110px] py-2 pr-2 whitespace-nowrap">Status</th>
                <th className="w-[80px] py-2 pr-2 whitespace-nowrap">Phase</th>
                <th className="w-[80px] py-2 pr-2 whitespace-nowrap text-right">Messages</th>
                <th className="w-[80px] py-2 pr-2 whitespace-nowrap text-right">Filtered</th>
                <th className="w-[170px] py-2 pr-2 whitespace-nowrap">Latest email sent at</th>
                <th className="w-[120px] py-2 pr-2 whitespace-nowrap">Domain Filter</th>
                <th className="w-[310px] py-2 pr-0 text-right whitespace-nowrap">Actions</th>
              </tr>
            </thead>
            <tbody>
              {visibleItems.length === 0 && <tr><td colSpan={10} className="py-4 text-muted-foreground">No matching connectors</td></tr>}
              {visibleItems.map((it: ConnectorDTO) => (
                <React.Fragment key={it.id}>
                  <tr className={isRetired(it) ? 'border-b bg-muted/10 text-muted-foreground' : 'border-b'}>
                    <td className="py-2 pr-2"><input type="checkbox" disabled={isImported(it) || isSendGrid(it) || isRetired(it)} checked={!!selected[it.id]} onChange={(e) => setSelected({ ...selected, [it.id]: e.target.checked })} aria-label={`Select ${it.email}`} /></td>
                    <td className="py-2 pr-2 whitespace-nowrap" title={providerName(it)}>
                      <span className={`inline-flex items-center gap-1.5${isRetired(it) ? ' grayscale' : ''}`}>
                        <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded bg-secondary text-secondary-foreground">
                          <Icon name={providerIcon(it)} />
                        </span>
                        {isImported(it) && (
                          <span className="rounded bg-muted px-2 py-0.5 text-xs text-muted-foreground" title="Imported via PST">Import</span>
                        )}
                      </span>
                    </td>
                    <td className="max-w-[300px] py-2 pr-2 font-medium truncate" title={it.email}>{it.email}</td>
                    <td className="py-2 pr-2">{isImported(it) ? '-' : <StatusBadge status={String(it.status || '-')} />}</td>
                    <td className="py-2 pr-2 whitespace-nowrap">{isImported(it) ? '-' : isSendGrid(it) ? 'Service' : (it.syncPhase ?? '-')}</td>
                    <td className="py-2 pr-2 text-right tabular-nums">{it.messageCount ?? 0}</td>
                    <td className="py-2 pr-2 text-right tabular-nums">{it.filteredMessageCount ?? 0}</td>
                    <td className="py-2 pr-2 whitespace-nowrap tabular-nums" title={String(it.latestSyncedEmailSentAt || '')}>
                      {formatBudapestDateTime(it.latestSyncedEmailSentAt as string | null | undefined)}
                    </td>
                    <td className="py-2 pr-2">
                      {isRetired(it) ? (
                        <span className="rounded bg-muted px-2 py-0.5 text-muted-foreground">Archived</span>
                      ) : isSendGrid(it) ? (
                        <span className="rounded bg-muted px-2 py-0.5 text-muted-foreground">Not applicable</span>
                      ) : it.domainFilter?.enabled ? (
                        <span className="rounded bg-secondary px-2 py-0.5 text-secondary-foreground">Enabled</span>
                      ) : (
                        <span className="rounded bg-muted px-2 py-0.5 text-muted-foreground">Disabled</span>
                      )}
                    </td>
                    <td className="py-2 pr-0 whitespace-nowrap">
                      {isRetired(it) ? (
                        <div className="flex flex-nowrap justify-end gap-0.5">
                          <Link className="inline-flex items-center rounded-md border px-2 py-1 text-xs hover:bg-accent" to={`/connectors/${encodeURIComponent(it.id)}/logs`} title="Open archived connector logs" aria-label="Open logs"><Icon name="logs" /></Link>
                          <Button size="xs" aria-label="Confirm retired sync is stopped" onClick={() => retire.mutate(it.id)} disabled={retire.isPending} title="Leállítás megerősítése / újrapróbálása"><Icon name="archive" /></Button>
                        </div>
                      ) : isImported(it) ? (
                        <div className="flex flex-nowrap justify-end gap-0.5">
                          <Link className="inline-flex items-center rounded-md border px-2 py-1 text-xs hover:bg-accent" to={`/connectors/${encodeURIComponent(it.id)}/logs`} title="Open logs" aria-label="Open logs"><Icon name="logs" /></Link>
                          <Button size="xs" variant="destructive" aria-label="Remove connector" onClick={() => { const purge = window.confirm('Also purge previously imported emails? Click OK to purge, Cancel to keep emails.'); remove.mutate({ id: it.id, purge }) }} disabled={remove.isPending} title="Remove imported connector (optionally purge messages)"><Icon name="trash" /></Button>
                        </div>
                      ) : isSendGrid(it) ? (
                        <div className="flex justify-end">
                          <span className="rounded bg-muted px-2 py-1 text-xs text-muted-foreground" title="Email delivery and SendGrid webhooks are managed by the Service">
                            Service managed
                          </span>
                        </div>
                      ) : (
                      <div className="flex flex-nowrap justify-end gap-0.5">
                        <Button size="xs" variant="primary" aria-label="Restart sync" onClick={() => restart.mutate(it.id)} disabled={restart.isPending} title="Restart sync">
                          <Icon name="restart" />
                        </Button>
                        <Button size="xs" aria-label="Trigger backfill" onClick={() => backfill.mutate(it.id)} disabled={backfill.isPending} title="Trigger backfill">
                          <Icon name="backfill" />
                        </Button>
                        {it.status === 'paused' ? (
                          <Button size="xs" aria-label="Resume syncing" onClick={() => resume.mutate(it.id)} disabled={resume.isPending} title="Resume syncing">
                            <Icon name="resume" />
                          </Button>
                        ) : (
                          <Button size="xs" aria-label="Pause syncing" onClick={() => pause.mutate(it.id)} disabled={pause.isPending} title="Pause syncing">
                            <Icon name="pause" />
                          </Button>
                        )}
                        <Button
                          size="xs"
                          onClick={() => {
                            if (editing === it.id) {
                              setEditing(null)
                            } else {
                              const df = it.domainFilter as any
                              setFilterForm({
                                enabled: Boolean(df?.enabled ?? true),
                                includePrimary: Boolean(df?.includePrimary ?? true),
                                extraDomains: Array.isArray(df?.extraDomains) ? df.extraDomains.join(' ') : '',
                                extraEmails: Array.isArray(df?.extraEmails) ? df.extraEmails.join(' ') : '',
                              })
                              setEditing(it.id)
                            }
                          }}
                          title="View & edit domain filter"
                        >
                          <Icon name="filter" />
                        </Button>
                        <Button size="xs" aria-label="Run audit" onClick={() => audit.mutate(it.id)} disabled={audit.isPending} title="Run consistency scan (detect & enqueue missing emails)"><Icon name="audit" /></Button>
                        <Link className="inline-flex items-center rounded-md border px-2 py-1 text-xs hover:bg-accent" to={`/connectors/${encodeURIComponent(it.id)}/logs`} title="Open logs" aria-label="Open logs"><Icon name="logs" /></Link>
                        {!isSendGrid(it) && <ReauthLink id={it.id} />}
                        {!isSendGrid(it) && <Button size="xs" aria-label="Copy shareable auth link" onClick={() => authLink.mutate(it.id)} disabled={authLink.isPending} title="Copy shareable auth link"><Icon name="link" /></Button>}
                        <Button size="xs" aria-label="Megszűntnek jelölés" onClick={() => { if (window.confirm(`${it.email}: megszűntnek jelölöd? A szinkron leáll, a connector és a levelei megmaradnak.`)) retire.mutate(it.id) }} disabled={retire.isPending} title="Megszűntnek jelölés — a levelek megmaradnak"><Icon name="archive" /></Button>
                        <Button size="xs" variant="destructive" aria-label="Remove connector" onClick={() => { if (!window.confirm(`Remove connector ${it.email}? Cancel leaves the connector and its emails unchanged.`)) return; const purge = window.confirm('Also purge previously synced emails? Click OK to purge, Cancel to keep emails.'); remove.mutate({ id: it.id, purge }) }} disabled={remove.isPending} title="Remove connector (optionally purge messages)"><Icon name="trash" /></Button>
                      </div>
                      )}
                    </td>
                  </tr>
                  {editing === it.id && !isImported(it) && !isSendGrid(it) && !isRetired(it) && (
                    <tr className="border-b bg-muted/20">
                      <td colSpan={10} className="p-3">
                        <div className="flex flex-col gap-3">
                          <div className="flex flex-wrap gap-3">
                            <label className="inline-flex items-center gap-2 text-sm"><input type="checkbox" checked={filterForm.enabled} onChange={(e) => setFilterForm({ ...filterForm, enabled: e.target.checked })} /> Enabled</label>
                            <label className="inline-flex items-center gap-2 text-sm"><input type="checkbox" checked={filterForm.includePrimary} onChange={(e) => setFilterForm({ ...filterForm, includePrimary: e.target.checked })} /> Include primary domain ({it.domainFilter.primaryDomain || '-'})</label>
                          </div>
                          <div className="grid gap-3 md:grid-cols-2">
                            <label className="flex flex-col gap-1 text-sm">
                              <span className="text-muted-foreground">Extra domains (comma/space separated)</span>
                              <textarea className="rounded-md border bg-background px-3 py-2" rows={2} value={filterForm.extraDomains} onChange={(e) => setFilterForm({ ...filterForm, extraDomains: e.target.value })} placeholder="example.com other.org" />
                            </label>
                            <label className="flex flex-col gap-1 text-sm">
                              <span className="text-muted-foreground">Extra emails (comma/space separated)</span>
                              <textarea className="rounded-md border bg-background px-3 py-2" rows={2} value={filterForm.extraEmails} onChange={(e) => setFilterForm({ ...filterForm, extraEmails: e.target.value })} placeholder="user@example.com other@org.com" />
                            </label>
                          </div>
                          <div className="flex justify-end gap-2">
                            <Button onClick={() => setEditing(null)} title="Discard domain filter changes">Cancel</Button>
                            <Button onClick={() => updateFilter.mutate({ id: it.id, enabled: filterForm.enabled, includePrimary: filterForm.includePrimary, extraDomains: filterForm.extraDomains, extraEmails: filterForm.extraEmails })} disabled={updateFilter.isPending} title="Save domain filter changes">Save</Button>
                            <Button
                              onClick={() => {
                                setRecomputeMode('auto')
                                setRecomputeStarted(false)
                                setRecomputeOpen({ id: it.id, email: it.email })
                              }}
                              disabled={recompute.isPending}
                              title="Recompute filter: reclassifies messages to match current filter"
                            >
                              Recompute filter
                            </Button>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <Modal open={!!recomputeOpen} onClose={() => { if (!recompute.isPending) setRecomputeOpen(null) }}>
        <div className="mb-2">
          <div className="text-lg font-medium">Recompute domain filter</div>
          <div className="text-sm text-muted-foreground">Reclassifies messages for <span className="font-medium">{recomputeOpen?.email}</span> based on the current domain filter. This runs in the background; the connector status switches to syncing and you can watch logs.</div>
        </div>
        <div className="my-3 grid gap-2">
          <label className="flex items-start gap-2 text-sm">
            <input type="radio" name="recompute-mode" checked={recomputeMode === 'auto'} onChange={() => setRecomputeMode('auto')} />
            <span>
              <span className="font-medium">Auto</span>
              <span className="block text-muted-foreground">Fast path that updates what’s needed based on current state.</span>
            </span>
          </label>
          <label className="flex items-start gap-2 text-sm">
            <input type="radio" name="recompute-mode" checked={recomputeMode === 'filtered-only'} onChange={() => setRecomputeMode('filtered-only')} />
            <span>
              <span className="font-medium">Filtered-only</span>
              <span className="block text-muted-foreground">Recheck messages that were previously minimized/filtered and reactivate if they now match.</span>
            </span>
          </label>
          <label className="flex items-start gap-2 text-sm">
            <input type="radio" name="recompute-mode" checked={recomputeMode === 'full'} onChange={() => setRecomputeMode('full')} />
            <span>
              <span className="font-medium">Full</span>
              <span className="block text-muted-foreground">Complete re-evaluation of all messages. Slowest; use when rules changed significantly.</span>
            </span>
          </label>
        </div>
        <div className="mt-2 text-sm">
          <label className="inline-flex items-start gap-2">
            <input type="checkbox" checked={recomputeRecheckDup} onChange={(e) => setRecomputeRecheckDup(e.target.checked)} />
            <span>
              <span className="font-medium">Duplikátumok újraellenőrzése</span>
              <span className="block text-muted-foreground">Ha korábban tévesen duplikátumnak minősültek levelek (azonos Message‑ID miatt), próbálja újraaktiválni a releváns példányokat.</span>
            </span>
          </label>
        </div>
        <div className="mt-4 flex items-center justify-between gap-3">
          <div className="text-xs text-muted-foreground">
            {recompute.isPending ? (
              <span className="inline-flex items-center gap-2"><Spinner /> Starting…</span>
            ) : recomputeStarted ? (
              <span>Started. You can follow progress in logs or the status column.</span>
            ) : (
              <span>Confirm to start. You can safely close this dialog after triggering.</span>
            )}
          </div>
          <div className="flex gap-2">
            <Button onClick={() => { if (!recompute.isPending) setRecomputeOpen(null) }}>Cancel</Button>
            <Button
              variant="primary"
              onClick={() => {
                if (!recomputeOpen) return
                setRecomputeStarted(false)
                recompute.mutate(
                  { id: recomputeOpen.id, mode: recomputeMode, recheckDuplicates: recomputeRecheckDup },
                  {
                    onSuccess: () => {
                      setRecomputeStarted(true)
                    },
                  }
                )
              }}
              disabled={recompute.isPending}
            >
              {recompute.isPending ? 'Starting…' : 'Start'}
            </Button>
            {recomputeOpen ? (
              <Link className="inline-flex items-center rounded-md border px-3 py-1.5 text-sm hover:bg-accent" to={`/connectors/${encodeURIComponent(recomputeOpen.id)}/logs`} onClick={() => setRecomputeOpen(null)}>
                View logs
              </Link>
            ) : null}
          </div>
        </div>
      </Modal>
      <Modal open={recomputeBulkOpen} onClose={() => { if (!bulkRecompute.isPending) setRecomputeBulkOpen(false) }}>
        <div className="mb-2">
          <div className="text-lg font-medium">Recompute domain filter (bulk)</div>
          <div className="text-sm text-muted-foreground">
            Reclassifies messages for <span className="font-medium">{selectedIds.length}</span> selected connector(s) based on the current domain filter.
            This runs in the background; you can monitor progress in the list and logs.
          </div>
        </div>
        <div className="my-3 grid gap-2">
          <label className="flex items-start gap-2 text-sm">
            <input type="radio" name="recompute-mode-bulk" checked={recomputeMode === 'auto'} onChange={() => setRecomputeMode('auto')} />
            <span>
              <span className="font-medium">Auto</span>
              <span className="block text-muted-foreground">Fast path that updates what’s needed based on current state.</span>
            </span>
          </label>
          <label className="flex items-start gap-2 text-sm">
            <input type="radio" name="recompute-mode-bulk" checked={recomputeMode === 'filtered-only'} onChange={() => setRecomputeMode('filtered-only')} />
            <span>
              <span className="font-medium">Filtered-only</span>
              <span className="block text-muted-foreground">Recheck messages that were previously minimized/filtered and reactivate if they now match.</span>
            </span>
          </label>
          <label className="flex items-start gap-2 text-sm">
            <input type="radio" name="recompute-mode-bulk" checked={recomputeMode === 'full'} onChange={() => setRecomputeMode('full')} />
            <span>
              <span className="font-medium">Full</span>
              <span className="block text-muted-foreground">Complete re-evaluation of all messages. Slowest; use when rules changed significantly.</span>
            </span>
          </label>
        </div>
        <div className="mt-2 text-sm">
          <label className="inline-flex items-start gap-2">
            <input type="checkbox" checked={recomputeRecheckDup} onChange={(e) => setRecomputeRecheckDup(e.target.checked)} />
            <span>
              <span className="font-medium">Duplikátumok újraellenőrzése</span>
              <span className="block text-muted-foreground">Ha korábban tévesen duplikátumnak minősültek levelek (azonos Message‑ID miatt), próbálja újraaktiválni a releváns példányokat.</span>
            </span>
          </label>
        </div>
        <div className="mt-4 flex items-center justify-between gap-3">
          <div className="text-xs text-muted-foreground">
            {bulkRecompute.isPending ? (
              <span className="inline-flex items-center gap-2"><Spinner /> Starting…</span>
            ) : recomputeBulkStarted ? (
              <span>Started. You can follow progress in logs or the status column.</span>
            ) : (
              <span>Confirm to start. You can safely close this dialog after triggering.</span>
            )}
          </div>
          <div className="flex gap-2">
            <Button onClick={() => { if (!bulkRecompute.isPending) setRecomputeBulkOpen(false) }}>Cancel</Button>
            <Button
              variant="primary"
              onClick={() => {
                if (!selectedIds.length) return
                setRecomputeBulkStarted(false)
                bulkRecompute.mutate({ ids: selectedIds, mode: recomputeMode, recheckDuplicates: recomputeRecheckDup })
              }}
              disabled={bulkRecompute.isPending || selectedIds.length === 0}
            >
              {bulkRecompute.isPending ? 'Starting…' : 'Start'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  )
}
